import { BrandMark } from "./ui";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getStoredUser } from "@/lib/auth";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { SPRING_NAV } from "@/lib/utils";
import type { AuthUser } from "@/lib/types";
import { FolderKanban, LockOpen, Menu, Smartphone, Users, X } from "lucide-react";
import { useLanguage } from "@/i18n";

/**
 * Dark charcoal sidebar — Slate & Field Green theme.
 *
 * Icons only by default, so the map and the Field data table get the width.
 * Tapping the menu button expands it OVER the page (the page does not shift);
 * choosing a link, tapping outside it, or Escape collapses it again.
 */
export default function Sidebar() {
  const pathname = useLocation().pathname || "/";
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setUser(getStoredUser());
  }, []);

  // Collapse on navigation, on a tap outside, and on Escape.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const isSurveyor = user?.role === "surveyor";
  const isProjectsActive = pathname === "/" || pathname.startsWith("/projects");
  const isAccountsActive = pathname.startsWith("/users");
  const isSessionsActive = pathname.startsWith("/sessions");
  const isReopenActive = pathname.startsWith("/reopen-requests");

  const navItems = [
    {
      key: "projects",
      to: "/",
      label: t.sidebar.projects,
      section: t.sidebar.workspace,
      icon: FolderKanban,
      active: isProjectsActive,
      show: true,
    },
    {
      key: "reopen",
      to: "/reopen-requests",
      label: t.sidebar.reopenRequests,
      section: t.sidebar.workspace,
      icon: LockOpen,
      active: isReopenActive,
      show: !isSurveyor,
    },
    {
      key: "accounts",
      to: "/users",
      label: t.sidebar.accounts,
      section: t.sidebar.administration,
      icon: Users,
      active: isAccountsActive,
      show: !isSurveyor,
    },
    {
      key: "sessions",
      to: "/sessions",
      label: t.sidebar.sessions,
      section: t.sidebar.administration,
      icon: Smartphone,
      active: isSessionsActive,
      show: !isSurveyor,
    },
  ].filter((i) => i.show);

  return (
    // Fixed narrow slot in the layout; the panel itself overlays when open.
    <div className="relative h-full w-14 flex-none">
      {/* While open, a backdrop catches the tap outside: it only closes the
          menu, so that tap can never also press a button underneath it. */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "absolute inset-y-0 left-0 z-40 flex flex-col overflow-hidden bg-chrome shadow-chrome select-none transition-[width] duration-200",
          open ? "w-56" : "w-14"
        )}
      >
        {/* Menu toggle + brand */}
        <div className="flex h-16 flex-none items-center gap-2 border-b border-chrome-border px-2">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? t.sidebar.collapseMenu : t.sidebar.expandMenu}
            title={open ? t.sidebar.collapseMenu : t.sidebar.expandMenu}
            className="flex h-10 w-10 flex-none items-center justify-center rounded-md text-chrome-text transition hover:bg-chrome-raised hover:text-ink-inverse"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          {open && (
            <div className="flex min-w-0 items-center gap-2">
              <BrandMark className="h-7 w-7 flex-none" />
              <div className="min-w-0">
                <p className="truncate font-display text-base font-bold uppercase tracking-[0.08em] text-ink-inverse">Sthapatya</p>
                <p className="truncate text-2xs font-medium text-chrome-muted">{t.sidebar.brandSubtitle}</p>
              </div>
            </div>
          )}
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-2 py-4">
          {navItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={item.key} className="space-y-1.5">
                {open ? (
                  (idx === 0 || navItems[idx - 1].section !== item.section) && (
                    <p className="truncate px-2 text-2xs font-semibold uppercase tracking-wider text-chrome-muted">
                      {item.section}
                    </p>
                  )
                ) : (
                  idx > 0 && <div className="mx-2 h-px bg-chrome-border" />
                )}
                <Link
                  to={item.to}
                  aria-current={item.active ? "page" : undefined}
                  title={open ? undefined : item.label}
                  aria-label={item.label}
                  className={cn(
                    "group relative flex h-10 items-center gap-2.5 rounded-md text-sm font-medium transition-colors",
                    open ? "px-3" : "justify-center",
                    item.active ? "text-white" : "text-chrome-text hover:bg-chrome-raised hover:text-ink-inverse"
                  )}
                >
                  {item.active && (
                    <motion.span
                      layoutId="nav-pill"
                      className="absolute inset-0 rounded-md bg-accent/15"
                      style={{ borderLeft: "3px solid #90E0EF" }}
                      transition={SPRING_NAV}
                      aria-hidden="true"
                    />
                  )}
                  <Icon
                    className={cn(
                      "relative z-10 h-5 w-5 flex-none transition-colors",
                      item.active ? "text-accent" : "text-chrome-muted group-hover:text-chrome-text"
                    )}
                  />
                  {open && <span className="relative z-10 truncate">{item.label}</span>}
                </Link>
              </div>
            );
          })}
        </nav>

        {/* Signed-in account: initial when collapsed, full box when open */}
        <div className="flex-none border-t border-chrome-border p-2">
          {open ? (
            <div className="rounded-md bg-chrome-raised/50 p-2.5">
              <p className="text-2xs font-medium text-ink-inverse">{t.sidebar.systemAccess}</p>
              <p className="mt-0.5 truncate text-2xs leading-relaxed text-chrome-muted">
                {user ? `${user.username} (${user.role})` : t.sidebar.signedIn}
              </p>
            </div>
          ) : (
            <div
              className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-chrome-raised text-xs font-semibold uppercase text-ink-inverse"
              title={user ? `${user.username} (${user.role})` : t.sidebar.signedIn}
            >
              {user?.username?.[0] ?? "?"}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
